import { ReactElement, useState, useEffect, useCallback } from 'react';
import {
  currencyFormat,
  getAvailableDates,
  getDateOptions,
  getEventDateDayWithMonth,
  getEventIdFromDate,
} from '../utils';
import { RadioGroup } from '@headlessui/react';
import classNames from 'classnames';
import Calendar from 'react-calendar';
import { AiOutlineCalendar } from 'react-icons/ai';
import { IoChevronBack } from 'react-icons/io5';
import BillingInfoForm, { BillingInformation } from './Form/BillingInfoForm';
import PayzenCardForm from './Form/PayzenCardForm';
import Button from './Button';
import Spinner from './Spinner';
import Modal from './Modal';
import { getAuth } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebaseApp } from '../helpers/firebase';
import { toastError } from './Common/toasters';
import { useRouter } from 'next/router';
import WingsCard from './WingsCard';

const auth = getAuth(getFirebaseApp());

const ticketsOptions = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10' },
];

type BookingModalProps = {
  events: EventType[];
  space: SpaceType;
  onClose: () => void;
};

const NEXT_PUBLIC_PAYZEN_USER = process.env.NEXT_PUBLIC_PAYZEN_USER as string;
const NEXT_PUBLIC_PAYZEN_MODE = process.env.NEXT_PUBLIC_PAYZEN_MODE as string;

const parseIdType = (idType: string) => {
  switch (idType) {
    case 'CITIZENSHIP_CARD':
      return 'CC';
    case 'RESIDENCE_PERMIT':
      return 'CE';
    case 'EMPRESA':
      return 'NIT';
    default:
      return 'CC';
  }
};

const BookingModal = ({
  space,
  events,
  onClose,
}: BookingModalProps): ReactElement => {
  // Step 0 - Tickets and Date
  // Step 1 - Billing Info
  // Step 2 - Payment Method
  const [loadingBillingInfo, setLoadingBillingInfo] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const availableDates = getAvailableDates(events);
  const datesOptions = getDateOptions(events);
  const [formToken, setFormToken] = useState('');
  const [savingBillingInfo, setSavingBillingInfo] = useState(false);
  const [showAllowanceModal, setShowAllowanceModal] = useState(false);
  const [user] = useAuthState(auth);
  const [ownedTickets, setOwnedTickets] = useState<TicketType[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const router = useRouter();
  const language = router.locale as SupportedLanguages;
  const isSpanish = language === 'es';

  const [ticketsNumber, setTicketsNumber] = useState(1);
  const [selectedDate, setSelectedDate] = useState(datesOptions[0]?.value);
  const [selectedEventId, setSelectedEventId] = useState(
    getEventIdFromDate(events, datesOptions[0]?.value as string)
  );

  const [showingCalendar, setShowingCalendar] = useState(
    selectedDate ? false : true
  );
  const [billingInformation, setBillingInformation] =
    useState<BillingInformation>({
      firstName: '',
      lastName: '',
      email: '',
      idType: '',
      idNumber: '',
      address: '',
      phoneNumber: {
        countryCode: '',
        countryDialCode: '',
        number: '',
      },
      orderId: '',
    });

  const selectedEvent: EventType | undefined = events.find(
    (event) => event.id === selectedEventId
  );

  useEffect(() => {
    setSelectedEventId(getEventIdFromDate(events, selectedDate));
  }, [events, selectedDate]);

  useEffect(() => {
    const getTickets = async () => {
      setLoadingTickets(true);
      try {
        const res = await fetch('/api/tickets');
        if (!res.ok) {
          throw new Error(res.statusText);
        }
        const data = await res.json();
        setOwnedTickets(
          data.results.filter((ticket: TicketType) => ticket.is_paid)
        );
      } catch (e) {
        console.log(e);
      }
      setLoadingTickets(false);
    };
    getTickets();
  }, [user]);

  useEffect(() => {
    const fetchBillingInfo = async () => {
      setLoadingBillingInfo(true);
      try {
        const response = await fetch(`/api/billing-info/${user?.email}`);
        const data = await response.json();
        setBillingInformation(data);
      } catch (e) {
        console.log(e);
      } finally {
        setLoadingBillingInfo(false);
      }
    };

    if (user?.email) {
      fetchBillingInfo();
    }
  }, [user?.email]);

  const toggleShowCalendar = () => {
    setShowingCalendar(!showingCalendar);
  };

  const handleSetSelectedDate = (date: Date) => {
    date.setHours(0, 0, 0, 0);
    setSelectedDate(date.toUTCString());

    setSelectedEventId(getEventIdFromDate(events, date.toUTCString()));
  };

  const handleBillingInfoSubmit = async (
    billingInfo: BillingInformation,
    error: any
  ) => {
    if (error) {
      toastError(
        'Lo sentimos, algo salió mal. Intenta de nuevo o contacta a soporte.'
      );
      return;
    }

    setSavingBillingInfo(true);
    setBillingInformation(billingInfo);
    try {
      const response = await fetch('/api/payzen/form-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          orderId: billingInfo.orderId,
          email: billingInfo.email,
          eventId: selectedEventId,
          quantity: ticketsNumber,
          spaceId: space.id,
          billingInfo,
          customer: {
            email: billingInfo.email,
            billingDetails: {
              identityType: parseIdType(billingInfo.idType || ''),
              identityCode: billingInfo.idNumber,
              firstName: billingInfo.firstName,
              lastName: billingInfo.lastName,
              phoneNumber: billingInfo.phoneNumber.number,
              address: billingInfo.address,
              // TODO: get this from user
              city: 'Bogota',
              zipCode: '110221',
            },
          },
        }),
      });
      const { formToken } = await response.json();
      setFormToken(formToken);
    } catch (e) {
      console.log(e);
    }
    if (Number(selectedEvent?.cover_price) !== 0) {
      setCurrentStep(2);
    }
    setShowAllowanceModal(true);
    setSavingBillingInfo(false);
  };

  const handleAcceptAllowance = useCallback(() => {
    setShowAllowanceModal(false);
    if (Number(selectedEvent?.cover_price) === 0) {
      router.push(`/purchase-success?orderId=${billingInformation.orderId}`);
    }
  }, [billingInformation.orderId, router, selectedEvent]);

  return (
    <div
      className={`
          flex
          flex-col
          items-center
          bg-white
          fixed
          bottom-0
          w-screen
          h-full
          overflow-x-hidden
          overflow-y-scroll
          z-50
          left-0
        `}
    >
      {savingBillingInfo && (
        <div className="flex flex-col justify-center items-center bg-black bg-opacity-60 fixed top-0 left-0 h-full w-full z-50">
          <Spinner />
        </div>
      )}
      <div className="bg-copper flex p-4 w-full justify-between items-center fixed top-0 z-50">
        <div className="flex gap-4 items-center">
          <div
            className="cursor-pointer text-white text-3xl"
            onClick={
              currentStep === 0
                ? onClose
                : () => {
                    setCurrentStep(0);
                  }
            }
          >
            <IoChevronBack />
          </div>
          <h2
            className={classNames('text-white  font-bold h-full', {
              'text-xl': space.name.length < 15,
              'text-lg': space.name.length > 15 && space.name.length < 20,
              'text-base': space.name.length > 20 && space.name.length < 24,
              'text-sm': space.name.length > 24,
            })}
          >
            {space.name}
          </h2>
        </div>
        {currentStep !== 2 && (
          <div className="bg-white text-copper py-2 px-4 rounded-full">
            {Number(selectedEvent?.cover_price) === 0
              ? isSpanish
                ? 'GRATIS'
                : 'FREE'
              : Number(selectedEvent?.cover_price) * ticketsNumber}
          </div>
        )}
      </div>
      <div className="w-full max-w-2xl grow">
        {/* Step 0 - Billing Information and Event/Tickets selection */}
        {(currentStep === 0 || currentStep === 1) && (
          <div className="flex flex-col pt-24 pb-10 px-6 gap-3 overflow-hidden">
            <div>
              <WingsCard
                first={space.name}
                second={getEventDateDayWithMonth(selectedDate as string)}
              />
              {!!selectedEvent && Number(selectedEvent?.cover_price) !== 0 && (
                <>
                  <p className="text-lg mt-2">
                    {isSpanish ? (
                      <>
                        <strong>¿Cuantas entradas</strong> quieres comprar?
                      </>
                    ) : (
                      <>
                        <strong>How many tickets</strong> do you want to buy?
                      </>
                    )}
                  </p>
                  <RadioGroup value={ticketsNumber} onChange={setTicketsNumber}>
                    <div className="py-4 flex gap-2 overflow-x-auto">
                      {ticketsOptions.map((to, idx) => (
                        <RadioGroup.Option value={to.value} key={idx}>
                          {({ checked }) => (
                            <div
                              className={classNames(
                                'cursor-pointer flex justify-center items-center h-12 w-12 rounded-full text-white',
                                {
                                  'bg-copper': checked,
                                  'bg-[#CACACA]': !checked,
                                }
                              )}
                            >
                              {to.label}
                            </div>
                          )}
                        </RadioGroup.Option>
                      ))}
                    </div>
                  </RadioGroup>
                </>
              )}
            </div>
            <div>
              <p className="text-lg pb-2">
                {isSpanish ? (
                  <>
                    <strong>¿Que fecha?</strong>, ¿para cuando?
                  </>
                ) : (
                  <>
                    <strong>Which date?</strong>, when?
                  </>
                )}
              </p>
              <RadioGroup value={selectedDate} onChange={setSelectedDate}>
                <div className="flex gap-2">
                  {datesOptions.map((to, idx) => (
                    <RadioGroup.Option value={to.value} key={idx}>
                      {({ checked }) => (
                        <div
                          className={classNames(
                            { 'bg-copper text-white': checked },
                            'cursor-pointer flex flex-shrink-0 justify-center items-center h-12 px-4 rounded-full border-2 border-gray-100'
                          )}
                        >
                          {to.label}
                        </div>
                      )}
                    </RadioGroup.Option>
                  ))}

                  {availableDates.length > 1 && (
                    <button
                      className={classNames(
                        'flex justify-center items-center h-12 rounded-full border-2 border-gray-100 px-2',
                        {
                          'text-black': !showingCalendar,
                          'text-copper': showingCalendar,
                        }
                      )}
                      onClick={toggleShowCalendar}
                    >
                      <AiOutlineCalendar
                        className="text-copper mr-1"
                        size="1.5em"
                      />
                      <span className="text-sm">
                        {isSpanish ? 'Otras fechas' : 'Other dates'}
                      </span>
                    </button>
                  )}
                </div>
              </RadioGroup>
              {showingCalendar && (
                <div className="flex w-full justify-center py-4">
                  <Calendar
                    locale="es-CO"
                    onChange={handleSetSelectedDate as any}
                    // TODO: set as selected date the first available event
                    value={selectedDate ? new Date(selectedDate) : undefined}
                    minDate={new Date()}
                    tileDisabled={({ activeStartDate, date, view }) => {
                      return !availableDates.some((ad) => ad.isSame(date));
                    }}
                  />
                </div>
              )}
            </div>
            {/* TODO: show billing info form just after user selects a date */}

            {currentStep === 0 && (
              <div className="w-full py-4 flex justify-center">
                <Button
                  onClick={() => {
                    setCurrentStep(1);
                  }}
                  disabled={!selectedDate}
                  id="continue-to-billing-info"
                >
                  {isSpanish ? 'Continuar' : 'Continue'}
                </Button>
              </div>
            )}
            {currentStep === 1 && (
              <div>
                <h2 className="font-semibold pb-8">
                  {isSpanish
                    ? 'Información de facturación'
                    : 'Billing Information'}
                </h2>
                {loadingBillingInfo ? (
                  <Spinner />
                ) : (
                  <BillingInfoForm
                    userEmail={user?.email as string}
                    eventId={selectedEventId}
                    quantity={ticketsNumber}
                    onSubmit={handleBillingInfoSubmit}
                    initialValues={billingInformation}
                    isFree={Number(selectedEvent?.cover_price) === 0} // if the event is free, show the form without payment
                  />
                )}
              </div>
            )}
          </div>
        )}
        {/* Step 1 - Payment method */}
        {currentStep === 2 && (
          <div className="flex flex-col pt-24 pb-10 px-7 gap-3 h-full">
            <div className="w-full h-full flex flex-col">
              {/* Payment section */}
              <h1 className="text-xl font-bold mb-2 text-copper">
                {isSpanish ? 'Resumen' : 'Summary'}
              </h1>
              <WingsCard
                first={space.name}
                second={getEventDateDayWithMonth(selectedDate as string)}
              />
              <div className="w-full flex flex-col mt-3">
                <div className="flex justify-between w-full">
                  <span>
                    {isSpanish ? 'Total entradas: ' : 'Total tickets: '}
                  </span>
                  <span>{ticketsNumber}</span>
                </div>
                <div className="flex justify-between w-full">
                  <span>{isSpanish ? 'Valor entrada:' : 'Ticket value:'}</span>
                  <span>
                    {currencyFormat(Number(selectedEvent?.cover_price))}
                  </span>
                </div>
                <div className="flex justify-between w-full">
                  <strong>
                    {isSpanish ? 'Valor total compra' : 'Total purchase'}
                  </strong>
                  <span className="text-copper">
                    {currencyFormat(
                      Number(selectedEvent?.cover_price) * ticketsNumber
                    )}
                  </span>
                </div>
              </div>
              <h2 className="font-bold text-xl mt-6">
                {isSpanish
                  ? 'Elige tu método de pago'
                  : 'Choose your payment method'}
              </h2>
              <PayzenCardForm formToken={formToken} key={'test'} />
            </div>
          </div>
        )}
      </div>
      <Modal
        show={showAllowanceModal}
        onClose={() => {
          setShowAllowanceModal(false);
        }}
        size="large"
        showHeader
        hideCloseButton
      >
        <div className="p-4">
          <p className="py-2">
            {isSpanish
              ? `¡Gracias por elegirnos! Nos enorgullece ofrecer una experiencia
              única a nuestros clientes y para lograrlo, nos reservamos el derecho
              de admisión y permanencia en el sitio.`
              : `Thank you
              for choosing us! We are proud to offer a unique experience to our
              customers and to achieve this, we reserve the right of admission and
              permanence on the site.`}
          </p>
          <p className="py-2">
            {isSpanish
              ? 'Por favor, asegúrate de cumplir con nuestro código de vestimenta antes de ingresar.'
              : 'Please make sure to comply with our dress code before entering.'}
          </p>
          {/* TODO: show dressing code here */}
          <div className="flex gap-2 py-4 flex-wrap justify-center">
            <Button
              type="secondary"
              onClick={() => {
                setShowAllowanceModal(false);
                setCurrentStep(1);
              }}
            >
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button type="primary" onClick={handleAcceptAllowance}>
              {isSpanish ? 'Continuar' : 'Continue'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BookingModal;
